/**
 * Created by jonas on 2020-10-01.
 */

import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData
} from "./SimpleReelModel";
import {FreespinSymbol} from "../../../game/reelsfs/FreespinSymbol";
import {DevilsCrossRoadGameConfig} from "../../../game/DevilsCrossRoadGameConfig";
import {gsap} from "gsap";
import {DevilsCrossRoadSound} from "../../../game/sound/DevilsCrossRoadSound";
import {DevilsCrossRoad} from "../../../game/DevilsCrossRoad";

export interface SimpleReelPaddingData {
    visualPaddingBefore:number;
    paddingBefore:number;
    visibleSymbols:number;
    paddingAfter:number;
    visualPaddingAfter:number;
}

export interface SymbolStopCallbackData {
    isNearWin:boolean;
    isSingleSymbolDrop:boolean;
}

export class SimpleSpinReelModel extends SimpleReelModel {
    private _triggerTime:number;
    public illegalVisualPaddingSymbols:string[] = [];
    public active:boolean = true;
    public collectable:boolean = true;
    public slowGoatTl:gsap.core.Timeline | undefined;
    public slowFakeGoatTl:gsap.core.Timeline | undefined;

    public isKilled:boolean = false;

    get reelSet():string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get stopTriggerTl():gsap.core.Timeline | undefined {
        return this._stopTriggerTl;
    }

    private _stopTriggerTl:gsap.core.Timeline | undefined;

    get stopTimeline():gsap.core.Timeline | undefined {
        return this._stopTimeline;
    }

    get speed():number {
        return this._speed;
    }

    set speed(value:number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    get state():SimpleReelModelState {
        return this._state;
    }

    set state(value:SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel:this.id, state:this._state});
        }
    }

    get symbols():SimpleReelModelSymbolData[] {
        return this._symbols;
    }


    //Fake reel
    private _reelSet:string[] | undefined;
    private _reelSetIndex:number = 0;

    private _stopIndex:number;
    private _stopSymbols:SimpleReelModelSymbolData[] = [];
    private _symbols:SimpleReelModelSymbolData[] = [];

    public paddingConfig:SimpleReelPaddingData = {
        visualPaddingBefore:1,
        paddingBefore:0,
        visibleSymbols:3,
        paddingAfter:0,
        visualPaddingAfter:2,
    }


    public animatedStepData = {
        x:0,
        y:0
    }

    public stepSize:{x:number, y:number} = {
        x:180,
        y:180
    };
    private _spinning:boolean = false;
    private _currentSpinStep:gsap.core.Tween | undefined;


    private animationTimeScale = 1;
    public stepDuration = 0.1;

    private _stopTimeline:gsap.core.Timeline | undefined;

    private _stopping:boolean = false;
    private triggerStop:boolean = false;


    private _startedReverse:boolean;


    private _speed:number = this.animationTimeScale / this.stepDuration;
    private masterSpinTimeline:gsap.core.Timeline | undefined;


    private _state:SimpleReelModelState = SimpleReelModelState.IDLE;


    public readonly id:number;
    public _delegate:SimpleReelModelDelegate;

    private goatSkipped:boolean = false;

    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events:APIEventSystem | undefined;

    constructor(id:number, delegate:SimpleReelModelDelegate, triggerEvents:boolean = false) {
        super(id, delegate, triggerEvents)
    }

    public setReelSet(reelSet:string[], index?:number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    private getNextSymbolName(nextSymbolPosition:number):string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];

    }

    private lockAndKillReel(): void {
        // Do not spin
        // just signal complete

    }

    private getNextSymbol(position:number, state:SimpleReelModelState):SimpleReelModelSymbolData {
        //Stopping
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state);
    }

    private getRandomSymbolVisualPaddingSymbolName():string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        let symbol = this.reelSet[randomIndex];
        if (this.illegalVisualPaddingSymbols.indexOf(symbol) >= 0) {
            symbol = this.getRandomSymbolVisualPaddingSymbolName();
        }
        return symbol;
    }

    private getRandomSymbolInReelSet():string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private getRandomSymbols(count:number, slowMo:boolean = false):string[] {
        const result:string[] = [];
        for (let i:number = 0; i < count; i++) {
            if(slowMo) {
                result.push("X");
            }
            else {
                result.push(this.getRandomSymbolVisualPaddingSymbolName());
            }
        }
        return result;
    }


    //Public functions¨
    public reInit(symbols:string[], goatReset:boolean = true) {
        this.forceRemoveSymbols();
        this._symbols.forEach((sym, rPos) => {
            (<FreespinSymbol>sym.positionable).resetToX();
            (<FreespinSymbol>sym.positionable).destroy();
        });
        this._stopSymbols.forEach((sym) => {
            (<FreespinSymbol>sym.positionable).destroy();
        });
    }

    public init(symbols:string[], goatReset:boolean = false) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);

        const totalCount = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        if(!goatReset) {
            symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
            symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));
        }

        for (let i:number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
        }

        if(!goatReset) {
            this.updatePosition();
        }
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public setAsStickyReel():gsap.core.Timeline{
        const tl = gsap.timeline({
            onStart:() => {
                this._triggerTime = Date.now();
            },
            onComplete:() => {
                this.state = SimpleReelModelState.STICKY;
                this.active = false; // TODO, is reset on skip/abort?
            }
        });

        return tl;
    }

    public unsetStickyReel():gsap.core.Timeline{
        const tl = gsap.timeline({
            onStart:() => {
                this._triggerTime = Date.now();
            },
            onComplete:() => {
                this.state = SimpleReelModelState.IDLE;
                this.active = true;
            }
        });

        return tl;
    }

    public addCloneSymbol(symbolName:string, value:number): gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        let symbol:SimpleReelModelSymbolData;
        let positionable:FreespinSymbol;

        tl.add(() => {
            this.init([symbolName]);
            symbol = this.getVisibleSymbolAt(0);
            positionable = <FreespinSymbol> symbol.positionable;
            positionable.addAndShowValueLabel(value.toString());
            this.setAsStickyReel();
        });
        tl.add(() => {
            positionable.blinkAndUpdateValue(value);
        });

        return tl;
    }

    public stop(stopSymbolNames:string[], reverse:boolean = false, isSticky:boolean = false , slowMo:boolean = false, slowMoTease:boolean = false):gsap.core.Timeline {
        reverse = this._startedReverse;
        this.goatSkipped = false;

        if (this.state != SimpleReelModelState.START && this.state != SimpleReelModelState.SPIN){
            return gsap.timeline();
        }

        let doSlowMo: boolean = slowMo;
        const slowMoDur: number = doSlowMo? 0.8 : 0;
        this.clearSymbols(this._stopSymbols);
        this._stopSymbols = [];
        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        let slowMoTeaseSym: FreespinSymbol | undefined;

        if(!isSticky) {

            // TODO: Reset for slowMo and Tease, get only "X" as padding symbols
            stopSymbolNames = (doSlowMo || slowMoTease)? this.getRandomSymbols(this.paddingConfig.visualPaddingBefore, true).concat(stopSymbolNames) : this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(stopSymbolNames);
            stopSymbolNames = (slowMoTease)?  stopSymbolNames.concat(["CX", "X"]) : stopSymbolNames.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

            for (let i: number = 0; i < stopSymbolNames.length; i++) {
                const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP, slowMoTease);

                if(doSlowMo || slowMoTease) {
                    symbol.positionable.position.y = -(DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT);
                    if(slowMoTease) {
                        slowMoTeaseSym = (<FreespinSymbol>symbol.positionable);
                    }
                }
                else {
                    symbol.positionable.position.y = this.stepSize.y * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
                }

                this._stopSymbols.push(symbol);
            }

        }


        this._stopTriggerTl = gsap.timeline({
            paused:true,
            onStart:() => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
                this._triggerTime = Date.now();
                if(doSlowMo) {
                    //this._delegate.hideMaskedNormal();
                }
                else {
                    this._delegate.showMaskedNormal();
                }
            }
        });

        this._stopTimeline = gsap.timeline({
            paused:true,
            onStart:() => {
                //  console.log(Date.now() - this._triggerTime);
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete:() => {
                this._stopTimeline = undefined;
                this._stopSymbols = [];
                this._stopping = false;

                if(!doSlowMo) {
                    this.state = SimpleReelModelState.STICKY;
                    this.state = SimpleReelModelState.STOPPED;
                    //this.state = SimpleReelModelState.IDLE;
                }
                //this.state = SimpleReelModelState.STICKY;
            }
        });

        if(doSlowMo) {
            if(slowMoTease) {
                this._stopTriggerTl.add(this.slowFakeGoatTl = this.slowGoatTease(), "<");
                if(slowMoTeaseSym) {
                    this._stopTriggerTl.add(() => {
                        if(slowMoTeaseSym) {
                            //slowMoTeaseSym?.resetTextures();
                        }
                    });
                }
            }
            else {
                this._stopTriggerTl.add(this.slowGoatTl =  this.slowGoatRubberBand(), "<");
            }
        }
        //let goatDur:number = goatTl.duration();


        if(!isSticky) {
            for (let i: number = 0; i < numberOfNormalSteps; i++) {
                const tween = this.step(reverse);
                this._stopTimeline.add(tween);
            }

            if(!doSlowMo) {
                this._stopTimeline.add(this.bounce(0.1, 0.5));
            }


            this._stopTriggerTl.add(gsap.to(this, (doSlowMo) ? {duration: this._stopTimeline.totalDuration() + slowMoDur} : {duration: this._stopTimeline.totalDuration()}));

        }

        return this._stopTriggerTl.play(0);
    }

    public forceSkipGoat(): void {
        if(this.slowGoatTl) {
            this.slowGoatTl.progress(1);
        }
        if(this.slowFakeGoatTl) {
            this.slowFakeGoatTl.progress(1);
        }
        this.goatSkipped = true;
    }

    public resetGoatTls(): void {
        if(this.slowGoatTl) {
            this.goatSkipped = true;
            this._delegate.show();
            this.slowGoatTl.progress(1);
            this.slowGoatTl.pause();
            this.slowGoatTl.kill();
            this.slowGoatTl = undefined;
        }

        if(this.slowFakeGoatTl) {
            this.goatSkipped = true;
            this._delegate.show();
            this.slowFakeGoatTl.progress(1);
            this.slowFakeGoatTl.pause();
            this.slowFakeGoatTl.kill();
            this.slowFakeGoatTl = undefined;
        }
    }

    public skipGoat():void {
        if(this.slowGoatTl) {
            this.goatSkipped = true;
            this._delegate.show();
            this.slowGoatTl.progress(1);
            this.slowGoatTl.pause();
            this.slowGoatTl.kill();
            this.slowGoatTl = undefined;
            this.state = SimpleReelModelState.STOPPED;
            this.state = SimpleReelModelState.STICKY;
        }
        if(this.slowFakeGoatTl) {
            this.goatSkipped = true;
            this._delegate.show();
            this.slowFakeGoatTl.progress(1);
            this.slowFakeGoatTl.pause();
            this.slowFakeGoatTl.kill();
            this.slowFakeGoatTl = undefined;
            this.state = SimpleReelModelState.STOPPED;
        }
    }

    public start(reverse:boolean = false):gsap.core.Timeline {
        let sticky:boolean = false;
        if(this.state === SimpleReelModelState.STICKY) {
            sticky = true;
            //this.active = false;
            //return gsap.timeline();
        }

        this._startedReverse = reverse;
        const tl:gsap.core.Timeline = gsap.timeline({
            onStart:() => {
                if(sticky) {
                    this.state = SimpleReelModelState.STICKY;
                }
                else {
                    this.state = SimpleReelModelState.START;
                    this._stopSymbols = [];
                }
            },
            onComplete:() => {
                if(sticky) {
                    this.state = SimpleReelModelState.STICKY;
                    this.masterSpinTimeline?.kill();
                    this._stopTimeline?.play(0);
                }
                else {
                    this.startSpin(reverse);
                }
            }
        });

/*
        const tween = this.step(reverse);
        tween.vars.ease = "back.in(3)";
        tween.totalDuration(this.stepDuration * 8);
*/

        if(!sticky) {
            const tween = this.windUp(0, reverse);
            tl.add(tween);
        }
        else {
            tl.progress(1);
        }

        return tl;
    }

    public windUp(windFactor:number = 0.2, reverse:boolean = false):gsap.core.Timeline {
        const tl = gsap.timeline();
        const wind = gsap.to(this.animatedStepData, {
            duration: (windFactor === 0)? this.stepDuration * 2 : this.stepDuration * 8,
            x:reverse ? this.stepSize.x * windFactor : -this.stepSize.x * windFactor,
            y:reverse ? this.stepSize.y * windFactor : -this.stepSize.y * windFactor,
            ease:"power2.inOut",
            onStart:() => {
                this.state = SimpleReelModelState.WIND_UP;
            },
            onUpdate:() => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * 6,
            x:0,
            y:0,
            ease:"power2.in",
            onUpdate:() => this.updatePosition(),
        });

        tl.add(wind);
        tl.add(back);

        return tl;
    }

    public slowGOAT():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        const slowDownTl = gsap.fromTo(this.animatedStepData,
            {
                y: -(DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT),
            },
            {
                duration: this.stepDuration * 25,
                x: 0,
                y: 0,
                onStart:() => {
                    this._delegate.show();
                    this.state = SimpleReelModelState.STOPPED;
                    this.state = SimpleReelModelState.STICKY;
                },
                //onUpdate:() => this.updatePosition(),
                onComplete:() => {
                    this._delegate.showMaskedNormal();
                }
        });

        tl.add(slowDownTl);

        return tl;
    }

    public slowGoatTease():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        // TODO: If fake goat, miss by moving it too far on y-axis.
        const slowDownTl = gsap.timeline().add(gsap.fromTo(this.animatedStepData,
            {
                y: -(DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT * 2)
            },
            {
                duration: this.stepDuration * 12.5, // 25
                x: 0,
                y:  -DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT - (DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT * 0.5),
                onStart:() => {
                    DevilsCrossRoadSound.soundPlayer.playTrack({name:DevilsCrossRoadSound.FS_DEVIL_COIN_ANTICIPATION});
                        this._delegate.show();
                        this._delegate.showMaskedNormal();
                    //this._delegate.show();
                    this.state = SimpleReelModelState.STOPPED;

                    //console.log("APPLIED GOAT TEASE!");
                },
                onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {
                    //this._delegate.showMaskedNormal();
                }
            })).add(gsap.fromTo(this.animatedStepData, {
                y: -DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT + (DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT * 0.5)
            },
            {
                ease: "power4.inOut",
                duration: this.stepDuration * 1.5,
                x: 0,
                y: DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT + 12,
                onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {
                    //this._delegate.showMaskedNormal();
                    //this.clearSymbols(this._stopSymbols);
                    //this.clearSymbols(this._symbols);
                }
            })).add(gsap.fromTo(this.animatedStepData, {
                y:  DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT + 12
            },
            {
                duration: this.stepDuration * 0.5,
                ease: "power2.out",
                x: 0,
                y: DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT + 2,
                //onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {
                    this.animatedStepData.x = 0;
                    this.animatedStepData.y = 0;
                    this.updatePosition();
                    //this._delegate.showMaskedNormal();
                    //this.state = SimpleReelModelState.STOPPED;
                    // this.state = SimpleReelModelState.STICKY;

                    //debugger;

                    //this._stopSymbols[0].positionable.position.y = (this.stepSize.y) * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols)
                    //this._symbols[1].positionable.position.y = -this.stepSize.y;
                    //this._symbols[1].positionable.position.y = 0;
                    //this._symbols[2].positionable.position.y = this.stepSize.y;
                    //this.updatePosition();
                    //debugger;
                }
            }));

        tl.add(slowDownTl);

        return tl;
    }

    public slowGoatRubberBand():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();


        // TODO: If fake goat, miss by moving it too far on y-axis.
        const slowDownTl = gsap.timeline().add(gsap.fromTo(this.animatedStepData,
            {
                y: -(DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT),
            },
            {
                duration: this.stepDuration * 12.5, // 25
                x: 0,
                y: 0 - (DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT*0.5),
                onStart:() => {
                    this.state = SimpleReelModelState.STOPPED;
                    this.state = SimpleReelModelState.STICKY;
                    DevilsCrossRoadSound.soundPlayer.playTrack({name:DevilsCrossRoadSound.FS_DEVIL_COIN_ANTICIPATION});
                    DevilsCrossRoadSound.soundPlayer.playTrack({name:DevilsCrossRoadSound.FS_DEVIL_COIN_LAND});
                },
                onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {

                }
            })).add(gsap.fromTo(this.animatedStepData, {
                y: 0 - (DevilsCrossRoadGameConfig.FS_SYMBOL_HEIGHT*0.5)
            },
            {
                duration: this.stepDuration * 0.5,
                ease: "power4.inOut",
                x: 0,
                y: 10,
                onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {
                }
            })).add(gsap.fromTo(this.animatedStepData, {
                y: 10
            },
            {
                duration: this.stepDuration * 0.5,
                ease: "power2.out",
                x: 0,
                y: 0,
                onUpdate: (!this.goatSkipped)? () => this.updatePosition() :  () => {},
                onComplete:() => {
                    DevilsCrossRoad.keypadPlugin.setZeroBetSpinCounter(DevilsCrossRoad.model.data.freespinsLeft);
                    DevilsCrossRoad.freespinUnlockAreas.setIndicator(DevilsCrossRoad.model.data.freespinsLeft);
                }
            }));

        tl.add(slowDownTl);

        return tl;
    }

    public bounce(bounceFactor:number = 0.1, durationFactor:number = 0.5, reverse:boolean = false):gsap.core.Timeline {
        const tl = gsap.timeline();
        const overshoot = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor,
            x:reverse ? -this.stepSize.x * bounceFactor : this.stepSize.x * bounceFactor,
            y:reverse ? -this.stepSize.y * bounceFactor : this.stepSize.y * bounceFactor,
            ease:"power2.out",
            onStart:() => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate:() => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor * 2,
            x:0,
            y:0,
            ease:"power2.out",
            onUpdate:() => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public nudge(reverse:boolean = false):gsap.core.Tween {
        const tween = this.step(reverse);
        tween.eventCallback("onStart", () => {
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
        });

        return tween;
    }

    private startSpin(reverse:boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse:boolean = false):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete:() => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }


    private step(reverse:boolean = false):gsap.core.Tween {
        const tween:gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration:this.stepDuration,
            x:reverse ? -this.stepSize.x : this.stepSize.x,
            y:reverse ? -this.stepSize.y : this.stepSize.y,
            ease:"none",
            onStart:() => this.onAnimationStart(tween, reverse),
            onUpdate:() => this.updatePosition(),
            onComplete:() => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween:gsap.core.Tween, reverse:boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse:boolean = false) {
        for (let i:number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.x = 0;
        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse:boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value:number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value:number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse:boolean) {
        let newSymbols = [];
        let newSymbolPosition = this._symbols[0].reelPosition - 1;

        if (reverse) {
            newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
        }

        newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a:any, b:any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    private addClonedSymbol() {
        let newSymbols = [];


        newSymbols.push(this.getNextSymbol(0, this.state));

        this._symbols = this._symbols.concat(newSymbols);
        //this._symbols.sort((a:any, b:any) => {
            //return (a.reelPosition < b.reelPosition) ? -1 : 1;
        //});
    }

    private removeSymbol() {
        for (let i:number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);

            }
        }
    }

    private removeAndDestroyAllSymbols() {
        //for (let i:number = this._symbols.length - 1; i >= 0; i--) {

            this._symbols.forEach((sym) => {
                this._delegate.removeSymbolReset(sym, true);
            });
        //}
    }


    public forceRemoveSymbols() {
        this.removeAndDestroyAllSymbols();
    }

    private updatePosition() {
        for (let i:number = 0; i < this._symbols.length; i++) {
            this.updatePositionForSymbol(this._symbols[i]);
        }
    }

    public forceUpdatePosition() {
        this.updatePosition();
    }

    private updatePositionForSymbol(symbol:SimpleReelModelSymbolData){
      /*  const basePosX = this.stepSize.x * symbol.reelPosition;
        symbol.positionable.position.x = basePosX + this.animatedStepData.x;*/

        const basePosY = this.stepSize.y * symbol.reelPosition;
        symbol.positionable.position.y = basePosY + this.animatedStepData.y;
    }

    public visibleIndexToRealIndex(visibleIndex:number) {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return visibleIndex + paddingBefore;
    }

    public getAllVisibleSymbols():SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        if (this._stopSymbols.length > 0) {
            return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        } else {
            return this._symbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        }
    }

    public getVisibleSymbolAt(symbolIndex:number):SimpleReelModelSymbolData {
        const index = this.visibleIndexToRealIndex(symbolIndex);

        if (this._stopSymbols.length > 0) {
            return this._stopSymbols[index];
        } else {
            return this._symbols[index];
        }
    }
}